import { useEffect, useState } from "react";
import { Link, useRoute } from "wouter";
import {
  CalendarDays,
  ChevronLeft,
  Eye,
  EyeOff,
  GitFork,
  Globe,
  MapPin,
  Plus,
  Trash2,
  Trophy,
  UserCheck,
  UserMinus,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useCreateTournament,
  useCurrentOrg,
  useDeleteEvent,
  useEvent,
  useEventTournaments,
  useMe,
  useRegisterEntrant,
  useRoster,
  useUnregisterEntrant,
  useUpdateEvent,
} from "@/lib/queries";
import { ApiError } from "@/lib/api";
import type {
  EventRegistration,
  Player,
  Tournament,
  TournamentFormat,
} from "@/lib/types";

export default function EventDetail() {
  const [, params] = useRoute("/dashboard/events/:slug");
  const { data: org } = useCurrentOrg();
  const slug = params?.slug ?? null;
  const { data: me } = useMe();
  const { data: event, isLoading } = useEvent(org?.slug ?? null, slug);
  const { data: roster = [] } = useRoster();
  const register = useRegisterEntrant(event?.id ?? "");
  const unregister = useUnregisterEntrant(event?.id ?? "");
  const del = useDeleteEvent();
  const [error, setError] = useState<string | null>(null);

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading event…</div>;
  }
  if (!event) {
    return (
      <div className="p-8 max-w-md mx-auto text-center space-y-3">
        <h1 className="text-2xl font-display uppercase">Not found</h1>
        <Button asChild>
          <Link href="/dashboard/events">Back to events</Link>
        </Button>
      </div>
    );
  }

  const myRole =
    me?.orgs.find((o) => o.orgSlug === org?.slug)?.role ?? null;
  const canEdit =
    me?.isPlatformAdmin || myRole === "owner" || myRole === "admin";

  const registeredPlayerIds = new Set(
    event.registrations
      .map((r) => r.playerId)
      .filter((id): id is string => !!id),
  );
  const unregisteredPlayers = roster.filter((p) => !registeredPlayerIds.has(p.id));

  async function registerPlayer(playerId: string) {
    setError(null);
    try {
      await register.mutateAsync({ playerId });
    } catch (e) {
      setError((e as ApiError).message ?? "Could not register");
    }
  }

  async function removeRegistration(registrationId: string) {
    setError(null);
    try {
      await unregister.mutateAsync(registrationId);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not unregister");
    }
  }

  async function deleteEvt() {
    if (!event) return;
    if (
      !confirm(
        `Delete "${event.name}"? Registrations and any tournaments under it will be removed.`,
      )
    ) {
      return;
    }
    try {
      await del.mutateAsync(event.id);
      window.history.back();
    } catch (e) {
      setError((e as ApiError).message ?? "Could not delete event");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto space-y-6">
      <Link
        href="/dashboard/events"
        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      >
        <ChevronLeft size={12} /> All events
      </Link>

      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight">
          {event.name}
        </h1>
        <p className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={13} />
            {formatRange(event.startDate, event.endDate)}
          </span>
          {event.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin size={13} /> {event.location}
            </span>
          )}
          <span className="text-[10px] font-display uppercase tracking-widest px-2 py-0.5 rounded-full border border-border">
            {event.format}
          </span>
        </p>
        {event.description && (
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {event.description}
          </p>
        )}
      </header>

      <RegistrationsCard
        registrations={event.registrations}
        canEdit={canEdit}
        onRemove={removeRegistration}
        removing={unregister.isPending}
      />

      {canEdit && (
        <AddEntrantsCard
          players={unregisteredPlayers}
          onAdd={registerPlayer}
          adding={register.isPending}
        />
      )}

      <TournamentsSection
        eventId={event.id}
        eventSlug={event.slug}
        canEdit={canEdit}
        registrationCount={event.registrations.length}
      />

      {canEdit && (
        <PublicVisibilitySection
          eventId={event.id}
          isPublic={event.isPublic}
          latitude={event.latitude}
          longitude={event.longitude}
          orgSlug={org?.slug ?? null}
          eventSlug={event.slug}
        />
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {canEdit && (
        <div className="flex items-center justify-end pt-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={deleteEvt}
            className="text-destructive hover:bg-destructive/10"
          >
            <Trash2 size={14} /> Delete event
          </Button>
        </div>
      )}
    </div>
  );
}

function RegistrationsCard({
  registrations,
  canEdit,
  onRemove,
  removing,
}: {
  registrations: EventRegistration[];
  canEdit: boolean;
  onRemove: (id: string) => void;
  removing: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserCheck size={18} /> Registered
          <span className="text-sm font-normal text-muted-foreground">
            ({registrations.length})
          </span>
        </CardTitle>
        <CardDescription>
          Players or teams entered in this event.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {registrations.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nobody registered yet.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {registrations.map((r) => {
              const label = r.player?.name ?? r.team?.name ?? "—";
              const sub = r.player ? `@${r.player.slug}` : r.team ? "team" : "";
              return (
                <li
                  key={r.id}
                  className="flex items-center gap-3 p-3 text-sm"
                >
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{label}</div>
                    {sub && (
                      <div className="text-xs text-muted-foreground">{sub}</div>
                    )}
                  </div>
                  {r.seed && (
                    <span className="text-[10px] font-display uppercase tracking-widest text-muted-foreground">
                      seed {r.seed}
                    </span>
                  )}
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={removing}
                      onClick={() => onRemove(r.id)}
                      aria-label="Unregister"
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <UserMinus size={14} />
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AddEntrantsCard({
  players,
  onAdd,
  adding,
}: {
  players: Player[];
  onAdd: (id: string) => void;
  adding: boolean;
}) {
  const [filter, setFilter] = useState("");
  const visible = filter
    ? players.filter(
        (p) =>
          p.name.toLowerCase().includes(filter.toLowerCase()) ||
          p.slug.includes(filter.toLowerCase()),
      )
    : players;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus size={18} /> Register a player
        </CardTitle>
        <CardDescription>
          Pulls from the org roster. Use{" "}
          <Link
            href="/dashboard/players"
            className="text-primary hover:underline"
          >
            Players
          </Link>{" "}
          to add new ones.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter…"
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {filter ? "No matches." : "Everyone on the roster is registered."}
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden max-h-72 overflow-y-auto">
            {visible.map((p) => (
              <li
                key={p.id}
                className="flex items-center gap-3 p-2.5 text-sm hover:bg-muted/50"
              >
                <div className="flex-1 min-w-0">
                  <div className="truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">@{p.slug}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={adding}
                  onClick={() => onAdd(p.id)}
                >
                  Add
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PublicVisibilitySection({
  eventId,
  isPublic,
  latitude,
  longitude,
  orgSlug,
  eventSlug,
}: {
  eventId: string;
  isPublic: boolean;
  latitude: number | null;
  longitude: number | null;
  orgSlug: string | null;
  eventSlug: string;
}) {
  const update = useUpdateEvent(eventId);
  const [lat, setLat] = useState(latitude != null ? String(latitude) : "");
  const [lng, setLng] = useState(longitude != null ? String(longitude) : "");
  const [err, setErr] = useState<string | null>(null);

  // Keep local state in sync if upstream refetch changes the values.
  useEffect(() => {
    setLat(latitude != null ? String(latitude) : "");
    setLng(longitude != null ? String(longitude) : "");
  }, [latitude, longitude]);

  async function toggle() {
    setErr(null);
    try {
      await update.mutateAsync({ isPublic: !isPublic });
    } catch (e) {
      setErr((e as ApiError).message ?? "Could not update");
    }
  }
  async function saveCoords() {
    setErr(null);
    const latN = lat.trim() === "" ? null : Number(lat);
    const lngN = lng.trim() === "" ? null : Number(lng);
    if (latN != null && (!Number.isFinite(latN) || latN < -90 || latN > 90)) {
      return setErr("Latitude must be -90 to 90.");
    }
    if (lngN != null && (!Number.isFinite(lngN) || lngN < -180 || lngN > 180)) {
      return setErr("Longitude must be -180 to 180.");
    }
    try {
      await update.mutateAsync({ latitude: latN, longitude: lngN });
    } catch (e) {
      setErr((e as ApiError).message ?? "Could not save coordinates");
    }
  }

  const publicUrl =
    orgSlug && isPublic
      ? `${typeof window !== "undefined" ? window.location.origin : ""}/events/${orgSlug}/${eventSlug}`
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe size={18} /> Public listing
        </CardTitle>
        <CardDescription>
          Public events show up on{" "}
          <Link href="/events" className="text-primary hover:underline">
            /events
          </Link>{" "}
          for cross-org discovery, get SEO-friendly metadata, and gain
          link-preview cards on social.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm inline-flex items-center gap-2">
            {isPublic ? (
              <>
                <Eye size={14} className="text-primary" />{" "}
                <span className="font-medium">Listed publicly</span>
              </>
            ) : (
              <>
                <EyeOff size={14} className="text-muted-foreground" />{" "}
                <span className="text-muted-foreground">Private</span>
              </>
            )}
          </div>
          <Button
            size="sm"
            variant={isPublic ? "outline" : "default"}
            onClick={toggle}
            disabled={update.isPending}
          >
            {isPublic ? "Make private" : "Make public"}
          </Button>
        </div>

        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline break-all inline-block"
          >
            {publicUrl}
          </a>
        )}

        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Coordinates (optional)
          </p>
          <p className="text-xs text-muted-foreground">
            Needed for the "Near me" filter on /events. Paste from Google
            Maps (right-click → copy lat/lng).
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              placeholder="Latitude"
            />
            <Input
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              placeholder="Longitude"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={saveCoords}
            disabled={update.isPending}
          >
            Save coordinates
          </Button>
        </div>

        {err && <p className="text-sm text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}

function TournamentsSection({
  eventId,
  eventSlug,
  canEdit,
  registrationCount,
}: {
  eventId: string;
  eventSlug: string;
  canEdit: boolean;
  registrationCount: number;
}) {
  const { data: tournaments = [] } = useEventTournaments(eventId);
  const [showForm, setShowForm] = useState(false);
  const create = useCreateTournament();
  const [name, setName] = useState("");
  const [format, setFormat] = useState<TournamentFormat>("single_elim");
  const [targetScore, setTargetScore] = useState(21);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setErr(null);
    if (!name.trim()) return setErr("Name required");
    try {
      const created = await create.mutateAsync({
        eventId,
        name: name.trim(),
        format,
        rules: { targetScore },
      });
      setShowForm(false);
      setName("");
      // Optimistic — the list query will refetch automatically.
      void created;
    } catch (e) {
      setErr((e as ApiError).message ?? "Could not create tournament");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitFork size={18} /> Tournaments
          <span className="text-sm font-normal text-muted-foreground">
            ({tournaments.length})
          </span>
        </CardTitle>
        <CardDescription>
          Brackets and pools that run inside this event. Players from the
          registered list above feed the bracket.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tournaments.length === 0 && !showForm && (
          <p className="text-sm text-muted-foreground">
            No tournaments yet. Add one to draft a bracket.
          </p>
        )}
        {tournaments.length > 0 && (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {tournaments.map((t: Tournament) => (
              <li key={t.id}>
                <Link
                  href={`/dashboard/events/${eventSlug}/t/${t.slug}`}
                  className="flex items-center gap-3 p-3 hover:bg-muted/50"
                >
                  <Trophy size={14} className="text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{t.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.format.replace("_", " ")} ·{" "}
                      <span className="font-display uppercase tracking-widest">
                        {t.status}
                      </span>
                      {t.bracketSize && ` · ${t.bracketSize} seeds`}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {canEdit && showForm && (
          <div className="space-y-3 rounded-md border border-border p-3">
            <Field label="Name">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Saturday Bracket"
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </Field>
            <Field label="Format">
              <div className="flex gap-2 flex-wrap">
                {(["single_elim", "round_robin"] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFormat(f)}
                    className={`px-3 py-2 rounded-md text-xs font-display uppercase tracking-widest border ${
                      format === f
                        ? "bg-foreground text-background border-foreground"
                        : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {f.replace("_", " ")}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Target score">
              <input
                type="number"
                min={7}
                max={99}
                value={targetScore}
                onChange={(e) => setTargetScore(Number(e.target.value) || 21)}
                className="w-24 rounded-md border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </Field>
            {err && <p className="text-sm text-destructive">{err}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={create.isPending}>
                {create.isPending ? "Creating…" : "Create tournament"}
              </Button>
            </div>
          </div>
        )}

        {canEdit && !showForm && (
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              {registrationCount < 2
                ? "Need at least 2 registrations before seeding."
                : `${registrationCount} registered — ready to seed.`}
            </p>
            <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
              <Plus size={14} /> Add
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}

function formatRange(start: string, end: string | null): string {
  const fmt = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return s;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  if (!end || end === start) return fmt(start);
  return `${fmt(start)} → ${fmt(end)}`;
}
